import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import Servidor from "../config/Servidor";
import "./ConfigurarSupervisor.css";

export default function ConfigurarSupervisor() {

  const { id } = useParams();
  const navigate = useNavigate();

  const [usuario, setUsuario] = useState(null);
  const [cobradores, setCobradores] = useState([]);

  const [busquedaCobrador, setBusquedaCobrador] =
    useState("");

  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

const [menuAbierto, setMenuAbierto] = useState(false);
const [modalMes, setModalMes] = useState(false);
const [modalBorrar, setModalBorrar] = useState(false);
const [mesesAgregar, setMesesAgregar] = useState("");


  // ==================================================
  // CARGAR USUARIO
  // ==================================================

  useEffect(() => {

    cargarUsuario();

  }, [id]);


  const cargarUsuario = async () => {

    try {

      setCargando(true);
      setError("");

      const res = await axios.get(
        `${Servidor.IP}/configurar/usuarios/${id}`
      );

      if (!res.data?.ok) {

        setError(
          res.data?.msg ||
          "No se pudo obtener el usuario"
        );

        return;
      }

      setUsuario(res.data.usuario);

      setCobradores(
        Array.isArray(res.data.cobradores)
          ? res.data.cobradores
          : []
      );

    } catch (err) {

      console.error(
        "Error obteniendo usuario:",
        err
      );

      setError(
        err.response?.data?.msg ||
        "Error al conectar con el servidor"
      );

    } finally {

      setCargando(false);

    }

  };


  // ==================================================
  // NOMBRE COMPLETO
  // ==================================================

  const nombreCompleto = (persona) => {

    return [
      persona?.nombre,
      persona?.primerapellido,
      persona?.segundoapellido
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

  };


  // ==================================================
  // ROL
  // ==================================================

  const obtenerRol = (usuario) => {

    if (usuario?.rol_nombre) {
      return usuario.rol_nombre;
    }

    switch (Number(usuario?.id_rol)) {

      case 1:
        return "Administrador";

      case 2:
        return "Supervisor";

      case 3:
        return "Cobrador";

      case 4:
        return "Independiente";

      default:
        return "Usuario";

    }

  };


  // ==================================================
  // MOSTRAR VALOR
  // ==================================================

  const mostrarValor = (valor) => {

    if (
      valor === null ||
      valor === undefined ||
      valor === ""
    ) {
      return "—";
    }

    return String(valor);

  };


  // ==================================================
  // NORMALIZAR TEXTO
  // ==================================================

  const normalizar = (valor) => {

    return String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  };


  // ==================================================
  // BUSCADOR DE COBRADORES
  // ==================================================

  const cobradoresFiltrados = useMemo(() => {

    const termino =
      normalizar(busquedaCobrador);

    if (!termino) {

      return cobradores;

    }

    return cobradores.filter((cobrador) => {

      const texto = normalizar([
        cobrador.id_usuario,
        cobrador.usuario,
        cobrador.nombre,
        cobrador.primerapellido,
        cobrador.segundoapellido,
        cobrador.cedula,
        cobrador.celular,
        cobrador.id_persona,
        cobrador.rol_nombre,
        Number(cobrador.activo) === 1
          ? "activo"
          : "inactivo"
      ].join(" "));

      return texto.includes(termino);

    });

  }, [cobradores, busquedaCobrador]);


  // ==================================================
  // ABRIR COBRADOR
  // ==================================================

  const abrirCobrador = (cobrador) => {

    navigate(
      `/configurarcobrador/${cobrador.id_usuario}`
    );

  };


  // ==================================================
  // CARGANDO
  // ==================================================

  if (cargando) {

    return (
      <div className="configurar-supervisor-page">

        <div className="configurar-supervisor-cargando">

          <div className="configurar-spinner"></div>

          <span>
            Cargando información...
          </span>

        </div>

      </div>
    );

  }


  // ==================================================
  // ERROR
  // ==================================================

  if (error || !usuario) {

    return (
      <div className="configurar-supervisor-page">

        <div className="configurar-supervisor-error">

          <div className="configurar-error-icon">
            ⚠
          </div>

          <h2>
            No se pudo cargar la información
          </h2>

          <p>
            {error || "Usuario no encontrado"}
          </p>

        </div>

      </div>
    );

  }


  const nombre =
    nombreCompleto(usuario) ||
    usuario.usuario ||
    "Sin nombre";

  const rol =
    obtenerRol(usuario);

    // ==================================================
// FORMATEAR FECHA
// ==================================================

const formatearFecha = (fecha) => {

  if (!fecha) {
    return "—";
  }

  const fechaObjeto =
    fecha instanceof Date
      ? fecha
      : new Date(fecha);

  if (isNaN(fechaObjeto.getTime())) {
    return String(fecha);
  }

  const dia = String(
    fechaObjeto.getDate()
  ).padStart(2, "0");

  const mes = String(
    fechaObjeto.getMonth() + 1
  ).padStart(2, "0");

  const anio =
    fechaObjeto.getFullYear();

  return `${dia}-${mes}-${anio}`;
};


// ==================================================
// AUMENTAR MES
// ==================================================

const aumentarMes = async () => {

  try {

    if (!window.confirm(
      "¿Deseas aumentar 1 mes al usuario?"
    )) {
      return;
    }

    const res = await axios.put(
      `${Servidor.IP}/configurar/usuarios/${id}/aumentar-mes`
    );

    if (!res.data?.ok) {

      alert(
        res.data?.msg ||
        "No se pudo aumentar el mes"
      );

      return;
    }

    alert("Mes aumentado correctamente");

    // Volvemos a cargar los datos actualizados
    await cargarUsuario();

  } catch (err) {

    console.error(
      "Error aumentando mes:",
      err
    );

    alert(
      err.response?.data?.msg ||
      "Error al aumentar el mes"
    );

  }

};


// ==================================================
// BORRAR USUARIO
// ==================================================

const borrarUsuario = async () => {

  try {

    if (!window.confirm(
      `¿Estás seguro de borrar al usuario "${nombre}"? Esta acción no se puede deshacer.`
    )) {
      return;
    }

    const res = await axios.delete(
      `${Servidor.IP}/configurar/usuarios/${id}`
    );

    if (!res.data?.ok) {

      alert(
        res.data?.msg ||
        "No se pudo borrar el usuario"
      );

      return;
    }

    alert("Usuario eliminado correctamente");

    navigate(-1);

  } catch (err) {

    console.error(
      "Error borrando usuario:",
      err
    );

    alert(
      err.response?.data?.msg ||
      "Error al borrar el usuario"
    );

  }

};

// ==================================================
// CALCULAR NUEVA FECHA DE VENCIMIENTO
// ==================================================

const calcularNuevaFecha = () => {

  if (
    !usuario?.fecha_vencimiento ||
    !mesesAgregar ||
    Number(mesesAgregar) < 1
  ) {
    return null;
  }

  const fecha = new Date(
    usuario.fecha_vencimiento
  );

  if (isNaN(fecha.getTime())) {
    return null;
  }

  fecha.setMonth(
    fecha.getMonth() + Number(mesesAgregar)
  );

  return fecha;
};



// ==================================================
// FECHA BONITA
// ==================================================

const fechaBonita = (fecha) => {

  if (!fecha) {
    return "—";
  }

  return fecha.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};


// ==================================================
// ABRIR MODAL DE MESES
// ==================================================

const abrirModalMes = () => {

  setMenuAbierto(false);
  setMesesAgregar("");
  setModalMes(true);

};



// ==================================================
// CONFIRMAR AUMENTO DE MESES
// ==================================================

const confirmarAumentoMes = async () => {

  try {

    const meses = Number(mesesAgregar);

    if (!mesesAgregar || meses < 1) {

      alert("Debes colocar al menos 1 mes.");

      return;
    }

    const res = await axios.put(
      `${Servidor.IP}/configurar/usuarios/${id}/aumentar-mes`,
      {
        meses
      }
    );

    if (!res.data?.ok) {

      alert(
        res.data?.msg ||
        "No se pudieron aumentar los meses."
      );

      return;
    }

    setModalMes(false);
    setMesesAgregar("");

    await cargarUsuario();

  } catch (err) {

    console.error(
      "Error aumentando meses:",
      err
    );

    alert(
      err.response?.data?.msg ||
      "Error al aumentar los meses."
    );

  }

};



// ==================================================
// ABRIR MODAL BORRAR
// ==================================================

const abrirModalBorrar = () => {

  setMenuAbierto(false);
  setModalBorrar(true);

};


// ==================================================
// CONFIRMAR BORRADO
// ==================================================

const confirmarBorrado = async () => {

  try {

    const res = await axios.delete(
      `${Servidor.IP}/configurar/usuarios/${id}`
    );

    if (!res.data?.ok) {

      alert(
        res.data?.msg ||
        "No se pudo borrar el usuario."
      );

      return;
    }

    setModalBorrar(false);

    navigate(-1);

  } catch (err) {

    console.error(
      "Error borrando usuario:",
      err
    );

    alert(
      err.response?.data?.msg ||
      "Error al borrar el usuario."
    );

  }

};


  return (
    <div className="configurar-supervisor-page">

      <div className="configurar-supervisor-container">


        {/* ==================================================
            ENCABEZADO
        ================================================== */}

  <div className="configurar-supervisor-header">

     <button
          className="volver-header"
           onClick={() => navigate(-1)}
        >
          <span className="flecha-volver">↩</span>
          <span>Volver</span>
        </button>

  <div>

    <h1 className="usuarios-title">
      Información del usuario
    </h1>

    <p>
      Datos completos de la cuenta
    </p>

  </div>

</div>



        {/* ==================================================
            PERFIL
        ================================================== */}

        <div className="configurar-supervisor-profile">

          <div className="configurar-supervisor-avatar">

            {nombre
              .charAt(0)
              .toUpperCase()}

          </div>


       <div className="configurar-supervisor-profile-info">

  <div className="configurar-supervisor-nombre-row">

    <h1>
      {nombre}
    </h1>

    <div className="configurar-supervisor-menu">

      <button
        type="button"
        className="configurar-supervisor-menu-btn"
        onClick={() =>
          setMenuAbierto(!menuAbierto)
        }
      >
        ⋮
      </button>


      {menuAbierto && (

        <div className="configurar-supervisor-menu-dropdown">

          <button
            type="button"
            onClick={abrirModalMes}
          >
            <span>＋</span>
            Aumentar meses
          </button>


          <button
            type="button"
            className="menu-borrar"
            onClick={abrirModalBorrar}
          >
            <span>🗑</span>
            Borrar usuario
          </button>

        </div>

      )}

    </div>

  </div>


  <p>
    @{usuario.usuario}
  </p>



            <div className="configurar-supervisor-badges">

              <span
                className={
                  `configurar-supervisor-rol rol-${Number(
                    usuario.id_rol
                  )}`
                }
              >
                {rol}
              </span>


              <span
                className={
                  Number(usuario.activo) === 1
                    ? "configurar-supervisor-activo"
                    : "configurar-supervisor-inactivo"
                }
              >
                <span className="estado-punto">
                  ●
                </span>

                {Number(usuario.activo) === 1
                  ? "Activo"
                  : "Inactivo"}

              </span>

            </div>

          </div>

        </div>

     {/* ==================================================
            DATOS PERSONALES
        ================================================== */}

        <section className="configurar-supervisor-section">

          <div className="configurar-supervisor-section-title">

            <div className="section-icon">
              📋
            </div>

            <div>

              <h2>
                Datos personales
              </h2>

              <p>
                Información registrada de la persona
              </p>

            </div>

          </div>


          <div className="configurar-supervisor-grid">


       


            <div className="configurar-supervisor-dato">
              <span>Cédula</span>
              <strong>
                {mostrarValor(
                  usuario.cedula
                )}
              </strong>
            </div>


            <div className="configurar-supervisor-dato">
              <span>Nombre</span>
              <strong>
                {mostrarValor(
                  usuario.nombre
                )}
              </strong>
            </div>


            <div className="configurar-supervisor-dato">
              <span>Primer apellido</span>
              <strong>
                {mostrarValor(
                  usuario.primerapellido
                )}
              </strong>
            </div>


            <div className="configurar-supervisor-dato">
              <span>Segundo apellido</span>
              <strong>
                {mostrarValor(
                  usuario.segundoapellido
                )}
              </strong>
            </div>


            <div className="configurar-supervisor-dato">
              <span>Celular</span>
              <strong>
                {mostrarValor(
                  usuario.celular
                )}
              </strong>
            </div>


            <div className="configurar-supervisor-dato">
              <span>Fecha nacimiento</span>
              <strong>
              {formatearFecha(usuario.fechan)}

              </strong>
            </div>


          </div>

        </section>
        {/* ==================================================
            DATOS DE USUARIO
        ================================================== */}

        <section className="configurar-supervisor-section">

          <div className="configurar-supervisor-section-title">

            <div className="section-icon">
              👤
            </div>

            <div>

              <h2>
                Datos del usuario
              </h2>

              <p>
                Información de la cuenta y configuración
              </p>

            </div>

          </div>


          <div className="configurar-supervisor-grid">


        


            <div className="configurar-supervisor-dato">
              <span>Usuario</span>
              <strong>
                {mostrarValor(
                  usuario.usuario
                )}
              </strong>
            </div>


            <div className="configurar-supervisor-dato">
              <span>Rol</span>
              <strong>
                {rol}
              </strong>
            </div>


         


            <div className="configurar-supervisor-dato">
              <span>Activo</span>
              <strong>
                {Number(usuario.activo) === 1
                  ? "Sí"
                  : "No"}
              </strong>
            </div>



            <div className="configurar-supervisor-dato">
              <span>Fecha</span>
              <strong>
              {formatearFecha(usuario.fecha)}

              </strong>
            </div>


            <div className="configurar-supervisor-dato">
              <span>Fecha vencimiento</span>
              <strong>
             {formatearFecha(usuario.fecha_vencimiento)}

              </strong>
            </div>


            <div className="configurar-supervisor-dato">
              <span>Meses pagados</span>
              <strong>
                {mostrarValor(
                  usuario.meses_pagados
                )}
              </strong>
            </div>


          </div>

        </section>


   


        {/* ==================================================
            COBRADORES
        ================================================== */}

        {Number(usuario.id_rol) === 2 && (

          <section className="configurar-supervisor-section">

            <div className="configurar-supervisor-section-title">

              <div className="section-icon cobradores-icon">
                👥
              </div>

              <div>

                <h2>
                  Cobradores
                </h2>

                <p>
                  Cobradores asignados a este supervisor
                </p>

              </div>

              <div className="configurar-cobradores-total">

                {cobradores.length}

              </div>

            </div>


            {/* ==================================================
                BUSCADOR COBRADORES
            ================================================== */}

            {cobradores.length > 0 && (

              <div className="configurar-cobradores-buscador">

                <span>
                  🔎
                </span>

                <input
                  type="text"
                  value={busquedaCobrador}
                  onChange={(e) =>
                    setBusquedaCobrador(
                      e.target.value
                    )
                  }
                  placeholder="Buscar cobrador por nombre, usuario, cédula, celular o ID..."
                />


                {busquedaCobrador && (

                  <button
                    type="button"
                    onClick={() =>
                      setBusquedaCobrador("")
                    }
                  >
                    ×
                  </button>

                )}

              </div>

            )}


            {/* ==================================================
                CONTADOR
            ================================================== */}

            {cobradores.length > 0 && (

              <div className="configurar-cobradores-contador">

                Mostrando{" "}
                <strong>
                  {cobradoresFiltrados.length}
                </strong>{" "}
                de{" "}
                <strong>
                  {cobradores.length}
                </strong>{" "}
                cobradores

              </div>

            )}


            {/* ==================================================
                LISTA
            ================================================== */}

            {cobradores.length === 0 ? (

              <div className="configurar-sin-cobradores">

                <div className="sin-cobradores-icon">
                  👥
                </div>

                <strong>
                  Sin cobradores asignados
                </strong>

                <span>
                  Este supervisor actualmente no tiene
                  cobradores asignados.
                </span>

              </div>

            ) : cobradoresFiltrados.length === 0 ? (

              <div className="configurar-sin-cobradores">

                <div className="sin-cobradores-icon">
                  🔍
                </div>

                <strong>
                  No se encontraron cobradores
                </strong>

                <span>
                  Prueba con otro nombre, usuario,
                  cédula, celular o ID.
                </span>

              </div>

            ) : (

              <div className="configurar-cobradores-lista">

                {cobradoresFiltrados.map(
                  (cobrador) => {

                    const nombreCobrador =
                      nombreCompleto(cobrador) ||
                      cobrador.usuario ||
                      "Sin nombre";


                    return (

                      <button
                        type="button"
                        className="configurar-cobrador-card"
                        key={cobrador.id_usuario}
                        onClick={() =>
                          abrirCobrador(
                            cobrador
                          )
                        }
                      >

                        <div className="configurar-cobrador-avatar">

                          {nombreCobrador
                            .charAt(0)
                            .toUpperCase()}

                        </div>


                        <div className="configurar-cobrador-info">

                          <div className="configurar-cobrador-nombre-row">

                            <h3>
                              {nombreCobrador}
                            </h3>

                            <span
                              className={
                                Number(
                                  cobrador.activo
                                ) === 1
                                  ? "mini-activo"
                                  : "mini-inactivo"
                              }
                            >
                              ●{" "}
                              {Number(
                                cobrador.activo
                              ) === 1
                                ? "Activo"
                                : "Inactivo"}
                            </span>

                          </div>


                          <p className="cobrador-usuario">
                            @{cobrador.usuario}
                          </p>


                          <div className="configurar-cobrador-datos">

                    

                            <span>
                              <b>Vencimiento:</b>{" "}
                              {mostrarValor(
                                cobrador.fecha_vencimiento
                              )}
                            </span>

                       

                      
                          </div>

                        </div>


                        <div className="configurar-cobrador-flecha">
                          →
                        </div>

                      </button>        

                    );

                  }
                )}

              </div>

            )}

          </section>

        )}

      </div>
{/* ==================================================
    MODAL AUMENTAR MESES
================================================== */}

{modalMes && (

  <div
    className="configurar-modal-overlay"
    onClick={() => setModalMes(false)}
  >

    <div
      className="configurar-modal"
      onClick={(e) => e.stopPropagation()}
    >

      <div className="configurar-modal-header">

        <div>

          <h2>
            Aumentar meses
          </h2>

          <p>
            Extiende la fecha de vencimiento del usuario.
          </p>

        </div>

        <button
          type="button"
          onClick={() => setModalMes(false)}
          className="configurar-modal-cerrar"
        >
          ×
        </button>

      </div>


      <div className="configurar-modal-body">

        <label>
          ¿Cuántos meses deseas agregar?
        </label>

      <input
  type="number"
  min="1"
  value={mesesAgregar}
  placeholder="Ej: 2"
  onChange={(e) => {
    setMesesAgregar(e.target.value);
  }}
/>



        <div className="configurar-fechas-preview">

          <div className="fecha-preview">

            <span>
              Vence actualmente
            </span>

            <strong>
              {formatearFecha(
                usuario.fecha_vencimiento
              )}
            </strong>

          </div>


          <div className="fecha-flecha">
            →
          </div>


          <div className="fecha-preview nueva">

            <span>
              Nuevo vencimiento
            </span>

            <strong>
              {formatearFecha(
                calcularNuevaFecha()
              )}
            </strong>

          </div>

        </div>

      </div>


      <div className="configurar-modal-footer">

        <button
          type="button"
          className="configurar-modal-cancelar"
          onClick={() => setModalMes(false)}
        >
          Cancelar
        </button>

        <button
          type="button"
          className="configurar-modal-confirmar"
          onClick={confirmarAumentoMes}
        >
          Aceptar y confirmar
        </button>

      </div>

    </div>

  </div>

)}
{/* ==================================================
    MODAL BORRAR USUARIO
================================================== */}

{modalBorrar && (

  <div
    className="configurar-modal-overlay"
    onClick={() => setModalBorrar(false)}
  >

    <div
      className="configurar-modal configurar-modal-borrar"
      onClick={(e) => e.stopPropagation()}
    >

      <button
        type="button"
        className="configurar-modal-cerrar borrar-cerrar"
        onClick={() => setModalBorrar(false)}
        title="Cerrar"
      >
        ×
      </button>


      <div className="configurar-modal-icono-borrar">
        🗑
      </div>


      <h2>
        ¿Borrar usuario?
      </h2>

      <p>
        Estás a punto de borrar a:
      </p>

      <strong className="configurar-modal-usuario">
        {nombre}
      </strong>

      <p className="configurar-modal-advertencia">
        Esta acción no se puede deshacer.
        Se eliminará el usuario y su información
        asociada.
      </p>


      <div className="configurar-modal-footer">

        <button
          type="button"
          className="configurar-modal-cancelar"
          onClick={() => setModalBorrar(false)}
        >
          Cancelar
        </button>

        <button
          type="button"
          className="configurar-modal-eliminar"
          onClick={confirmarBorrado}
        >
          Sí, borrar usuario
        </button>

      </div>

    </div>

  </div>

)}


    </div>
  );
}